import { useEffect, useRef, useState } from 'react';
import { AppHeader, EmptyState, Fraction, ProgressBar, VoiceWarning } from '../components/common';
import { Bi } from '../components/Bilingual';
import { QuestionImage } from '../components/QuestionImage';
import { SpeakButton } from '../components/SpeakButton';
import { useT } from '../hooks/useT';
import { getEntry } from '../services/progress';
import { scoreQuestion } from '../services/scheduler';
import { useBank } from '../state/BankContext';
import { useProgress } from '../state/ProgressContext';
import { useRouter } from '../state/RouterContext';
import { useSettings } from '../state/SettingsContext';
import { useTts } from '../state/TtsContext';

/**
 * "Learn Road Signs" — a flash-card deck.
 *
 * Sign first, big. The learner listens, tries to remember, reveals the meaning,
 * then says whether they knew it. Recognising signs is mostly repetition, so
 * signs marked "needs practice" come back first next time.
 */
export function SignsLearnScreen() {
  const t = useT();
  const { signs } = useBank();
  const { progress, answer } = useProgress();
  const { settings, uiLang } = useSettings();
  const { speak, stop, canSpeak, canSpeakText, speechLang } = useTts();
  const { home } = useRouter();

  // Order the deck once: weakest signs first, with a little randomness.
  const [deck] = useState(() => {
    const now = Date.now();
    return [...signs].sort(
      (a, b) =>
        scoreQuestion(getEntry(progress, 'sign', b.id), now) -
        scoreQuestion(getEntry(progress, 'sign', a.id), now),
    );
  });

  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState(false);
  const [knownCount, setKnownCount] = useState(0);
  /** Same double-tap guard as the quiz: a ref moves immediately, state batches. */
  const indexRef = useRef(0);

  const sign = deck[index];

  useEffect(() => () => stop(), [stop]);

  // When the meaning is revealed, read it out loud.
  useEffect(() => {
    if (!revealed || !sign || !canSpeak || !settings.autoSpeakQuestion) return;
    const text = speechLang === 'ar' ? `${sign.nameAr}. ${sign.meaningAr}` : `${sign.nameEn}. ${sign.meaningEn}`;
    const timer = window.setTimeout(() => speak(text, speechLang, 'meaning'), 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, sign?.id, canSpeak, speechLang, settings.autoSpeakQuestion]);

  if (deck.length === 0) {
    return (
      <>
        <AppHeader title={t('learnSigns')} />
        <main id="main" className="app-main">
          <EmptyState message={t('noQuestionsYet')} />
        </main>
      </>
    );
  }

  const grade = (knew: boolean) => {
    stop();
    answer('sign', sign.id, knew);
    if (knew) setKnownCount((c) => c + 1);
    const current = indexRef.current;
    if (current + 1 >= deck.length) {
      setDone(true);
      return;
    }
    indexRef.current = current + 1;
    setIndex(current + 1);
    setRevealed(false);
  };

  if (done) {
    return (
      <>
        <AppHeader title={t('learnSigns')} />
        <main id="main" className="app-main stack">
          <section className="card stack" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem' }} aria-hidden="true">
              🎉
            </div>
            <h2>{t('greatJob')}</h2>
            <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>
              <Fraction value={knownCount} max={deck.length} />
            </div>
            <ProgressBar value={knownCount} max={deck.length} tone="success" />
            <div className="row" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn--primary btn--big"
                onClick={() => {
                  indexRef.current = 0;
                  setIndex(0);
                  setRevealed(false);
                  setKnownCount(0);
                  setDone(false);
                }}
              >
                {t('startAgain')}
              </button>
              <button type="button" className="btn" onClick={home}>
                {t('goHome')}
              </button>
            </div>
          </section>
        </main>
      </>
    );
  }

  const meaningText = speechLang === 'ar' ? `${sign.nameAr}. ${sign.meaningAr}` : `${sign.nameEn}. ${sign.meaningEn}`;

  return (
    <>
      <AppHeader title={t('learnSigns')} />
      <main id="main" className="app-main stack">
        <VoiceWarning />
        <ProgressBar
          value={index + 1}
          max={deck.length}
          label={`${t('signOf')} ${index + 1} ${t('ofWord')} ${deck.length}`}
        />

        <section className="card question-card">
          <QuestionImage
            src={sign.image}
            altAr={`إشارة ${sign.nameAr}`}
            altEn={`${sign.nameEn} sign`}
          />

          {!revealed ? (
            <>
              <h2 className="question-text" style={{ textAlign: 'center' }}>
                {t('whatDoesSignMean')}
              </h2>
              <button
                type="button"
                className="btn btn--primary btn--big btn--block"
                onClick={() => setRevealed(true)}
              >
                <span className="btn__icon" aria-hidden="true">
                  👁
                </span>
                {t('showMeaning')}
              </button>
            </>
          ) : (
            <>
              <div className="question-head">
                <SpeakButton
                  id="meaning"
                  text={`${sign.nameAr}. ${sign.meaningAr}`}
                  textEn={`${sign.nameEn}. ${sign.meaningEn}`}
                  size="lg"
                  label={t('listenAnswer')}
                />
                <div style={{ flex: 1 }}>
                  <h2 className="question-text" style={{ marginBottom: 'var(--space-2)' }}>
                    <Bi ar={sign.nameAr} en={sign.nameEn} as="span" enClassName="question-text--en" />
                  </h2>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                    <Bi ar={sign.meaningAr} en={sign.meaningEn} as="div" enClassName="answer__en" />
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn btn--block"
                onClick={() => speak(meaningText, speechLang, 'meaning')}
                disabled={!canSpeakText(meaningText, speechLang)}
              >
                <span className="btn__icon" aria-hidden="true">
                  🔁
                </span>
                {t('listenAgain')}
              </button>

              <div className="row">
                <button type="button" className="btn btn--success btn--big" style={{ flex: 1 }} onClick={() => grade(true)}>
                  {t('markLearned')}
                </button>
                <button type="button" className="btn btn--big" style={{ flex: 1 }} onClick={() => grade(false)}>
                  {t('markPractice')}
                </button>
              </div>
            </>
          )}
        </section>

        {!settings.easyMode ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center' }}>
            {uiLang === 'ar' ? sign.source : sign.source}
          </p>
        ) : null}
      </main>
    </>
  );
}
